/**
 * ListingsDrawer styles
 *
 * Styles for the embedded Listings section. The component renders into a host
 * (UnifiedDrawer); the host owns the wrapper, handle, panel, and chrome.
 */
// Shared with ListingsDrawer.ts's computeColumnCount(), which needs the same
// two numbers in JS to figure out how many cards to include in a collapsed
// neighborhood group's preview - single source of truth so the JS-computed
// count can't drift from what the CSS grid actually fits.
export const MIN_CARD_COL_WIDTH = 260;
export const CARD_GRID_GAP = 8;
export const listingsDrawerStyles = `
/* Embedded mode: render the body inline into the host with no absolute chrome */
.listings-embedded {
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: inherit;
}
.listings-embedded input,
.listings-embedded button,
.listings-embedded label,
.listings-embedded select { font-family: inherit; }

.listings-drawer-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px;
}

/* Card lists flow into as many columns as fit (2, then 3, etc.) once the
   drawer is wide enough for more than one MIN_CARD_COL_WIDTH-wide column -
   grid's default row-major auto-flow keeps left-to-right-then-next-row
   order matching whatever sort/priority order the cards were emitted in,
   so no extra JS bookkeeping is needed for placement. */
.listings-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${MIN_CARD_COL_WIDTH}px, 1fr));
  gap: ${CARD_GRID_GAP}px;
}

.listing-card {
  display: flex;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.listing-card:hover {
  background: #f0fdf4;
  border-color: #22c55e;
}

.listing-card-image {
  width: 80px;
  height: 60px;
  border-radius: 6px;
  object-fit: cover;
  background: #e5e7eb;
  flex-shrink: 0;
}

.listing-card-image-placeholder {
  width: 80px;
  height: 60px;
  border-radius: 6px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  flex-shrink: 0;
}

.listing-card-image-container {
  position: relative;
  flex-shrink: 0;
}

/* Action buttons on right side of card */
.listing-card-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  padding-top: 2px;
  flex-shrink: 0;
}

.listing-action-btn {
  width: 28px;
  height: 28px;
  padding: 5px;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: color 0.15s, background 0.15s, transform 0.15s;
}

.listing-action-btn svg {
  width: 16px;
  height: 16px;
}

.listing-action-heart:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.listing-action-heart.is-favorite {
  color: #ef4444;
}

/* Amber at rest (not just on hover/has-alert) so it reads as a distinct,
   inviting action rather than the same neutral gray as the heart/calendar
   icons beside it. has-alert inverts to a solid filled dot, same
   outline-vs-filled convention as the modals' alert button. */
.listing-action-bell {
  color: #d97706;
}

.listing-action-bell:hover {
  color: #b45309;
  background: rgba(245, 158, 11, 0.15);
}

.listing-action-bell.has-alert {
  color: #ffffff;
  background: #f59e0b;
}

.listing-action-bell.has-alert:hover {
  background: #d97706;
}

.listing-action-calendar:hover {
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.1);
}

.listing-card-info {
  flex: 1;
  min-width: 0;
}

/* Price row with badges inline */
.listing-card-price-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-bottom: 4px;
}

.listing-card-price {
  font-weight: 600;
  color: #22c55e;
  font-size: 1rem;
  flex-shrink: 0;
}

.listing-card-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.listing-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.listing-badge.new {
  background: #dcfce7;
  color: #16a34a;
}

.listing-badge.open-house {
  background: #ede9fe;
  color: #7c3aed;
}

.listing-badge.price-change {
  background: #dbeafe;
  color: #1d4ed8;
}

.listing-badge.virtual-tour {
  background: #fef3c7;
  color: #b45309;
}

.listing-card-address {
  font-size: 0.85rem;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.listing-card-neighborhood {
  font-size: 0.75rem;
  color: #16a34a;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.listing-card-stats {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: #666;
}

.listing-card-stat {
  display: flex;
  align-items: center;
  gap: 4px;
}

.listings-drawer-empty {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.listings-drawer-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  padding: 48px 20px;
  color: #666;
}

.listings-drawer-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #e0e0e0;
  border-top-color: #666;
  border-radius: 50%;
  animation: listings-drawer-spin 0.8s linear infinite;
}

@keyframes listings-drawer-spin {
  to { transform: rotate(360deg); }
}

.listings-drawer-overflow-notice {
  text-align: center;
  padding: 16px 20px;
  color: #666;
  font-size: 0.85rem;
}

/* Neighborhood grouping */
.listings-group {
  margin-bottom: 16px;
}

.listings-group:last-child {
  margin-bottom: 0;
}

.listings-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f3f4f6;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.listings-group-header:hover {
  background: #e5e7eb;
}

.listings-group-header.other {
  cursor: default;
  background: #fafafa;
}

.listings-group-header.other:hover {
  background: #fafafa;
}

.listings-group-name {
  font-weight: 600;
  font-size: 0.85rem;
  color: #333;
}

.listings-group-count {
  background: #16a34a;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
}

.listings-group-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${MIN_CARD_COL_WIDTH}px, 1fr));
  gap: ${CARD_GRID_GAP}px;
}

.listings-group-content.collapsed {
  display: none;
}

.listings-show-more {
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  color: #6b7280;
  font-size: 13px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
}

.listings-show-more:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #374151;
}

/* "Show All" link in collapsed neighborhood headers */
.listings-show-all {
  font-size: 0.7rem;
  color: #16a34a;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
}

.listings-show-all:hover {
  text-decoration: underline;
}

/* Compact header when collapsed with preview */
.listings-group-header.collapsed-preview {
  padding: 4px 10px;
  margin-bottom: 6px;
}

/* Enhanced group header with expand/collapse and controls */
.listings-group-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
}

.listings-group-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.listings-group-chevron {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: #6b7280;
  transition: transform 0.2s;
}

.listings-group-chevron.expanded {
  transform: rotate(90deg);
}

/* Heart and bell buttons for neighborhoods */
.listings-nh-heart,
.listings-nh-bell {
  width: 24px;
  height: 24px;
  padding: 4px;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: color 0.15s, background 0.15s;
}

.listings-nh-heart svg,
.listings-nh-bell svg {
  width: 14px;
  height: 14px;
}

.listings-nh-heart:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.listings-nh-heart.is-favorite {
  color: #ef4444;
}

.listings-nh-bell {
  color: #d97706;
}

.listings-nh-bell:hover {
  color: #b45309;
  background: rgba(245, 158, 11, 0.15);
}

.listings-nh-bell.has-alert {
  color: #ffffff;
  background: #f59e0b;
}

.listings-nh-bell.has-alert:hover {
  background: #d97706;
}

/* Attribute sections */
.listings-attribute-section {
  margin-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 12px;
}

.listings-attribute-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.listings-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 10px;
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 6px;
  transition: background 0.2s;
}

.listings-section-header:hover {
  background: linear-gradient(135deg, #dcfce7, #bbf7d0);
}

/* Sort header bar (always visible) */
.listings-sort-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  margin: -12px -12px 12px -12px;
  background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: -12px;
  z-index: 5;
}

.listings-sort-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
}

.listings-sort-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.listings-filters-indicator {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.listings-filters-indicator.on {
  background: #fef3c7;
  color: #b45309;
}

/* Extra header button (e.g., 📊 Stats) */
.listings-extra-btn {
  padding: 4px 8px;
  font-size: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  cursor: pointer;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.listings-extra-btn:hover {
  border-color: #2c5530;
  color: #2c5530;
  background: #f0fdf4;
}

/* Sort dropdown */
.listings-sort-dropdown {
  padding: 4px 8px;
  font-size: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  color: #374151;
  cursor: pointer;
  outline: none;
  font-weight: 500;
  position: relative;
  z-index: 10;
  -webkit-appearance: menulist;
  appearance: menulist;
}

.listings-sort-dropdown:hover {
  border-color: #9ca3af;
  background: #f9fafb;
}

.listings-sort-dropdown:focus {
  border-color: #22c55e;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.2);
}

/* Agent Picks selector - "Current View" or switch to a past Agent Picks
   list (see ListingsDrawerOptions.agentPicksMenu). Same orange accent as
   UnifiedDrawer's divider-tint-when-active. */
.listings-agent-picks-select {
  padding: 4px 8px;
  font-size: 0.75rem;
  border: 1px solid #fdba74;
  border-radius: 6px;
  background: #fff7ed;
  color: #c2410c;
  cursor: pointer;
  outline: none;
  font-weight: 500;
  max-width: 140px;
  -webkit-appearance: menulist;
  appearance: menulist;
}

.listings-agent-picks-select:hover {
  border-color: #f97316;
}

.listings-agent-picks-select:focus {
  border-color: #f97316;
  box-shadow: 0 0 0 2px rgba(249, 115, 22, 0.2);
}

/* Colored section headers by type */
.listings-section-header.section-favorites {
  background: linear-gradient(135deg, #fef2f2, #fecaca);
}
.listings-section-header.section-favorites:hover {
  background: linear-gradient(135deg, #fecaca, #fca5a5);
}
.listings-section-header.section-favorites .listings-section-chevron { color: #ef4444; }
.listings-section-header.section-favorites .listings-section-count { background: #ef4444; }

.listings-section-header.section-alerts {
  background: linear-gradient(135deg, #fffbeb, #fed7aa);
}
.listings-section-header.section-alerts:hover {
  background: linear-gradient(135deg, #fed7aa, #fdba74);
}
.listings-section-header.section-alerts .listings-section-chevron { color: #f59e0b; }
.listings-section-header.section-alerts .listings-section-count { background: #f59e0b; }

.listings-section-header.section-newListing {
  background: linear-gradient(135deg, #fefce8, #fef08a);
}
.listings-section-header.section-newListing:hover {
  background: linear-gradient(135deg, #fef08a, #fde047);
}
.listings-section-header.section-newListing .listings-section-chevron { color: #eab308; }
.listings-section-header.section-newListing .listings-section-count { background: #eab308; }

.listings-section-header.section-priceChange {
  background: linear-gradient(135deg, #eff6ff, #bfdbfe);
}
.listings-section-header.section-priceChange:hover {
  background: linear-gradient(135deg, #bfdbfe, #93c5fd);
}
.listings-section-header.section-priceChange .listings-section-chevron { color: #3b82f6; }
.listings-section-header.section-priceChange .listings-section-count { background: #3b82f6; }

.listings-section-header.section-openHouse {
  background: linear-gradient(135deg, #f5f3ff, #ddd6fe);
}
.listings-section-header.section-openHouse:hover {
  background: linear-gradient(135deg, #ddd6fe, #c4b5fd);
}
/* #a78bfa matches the shared attribute registry's open house color - a
   brighter violet than this section originally shipped with. */
.listings-section-header.section-openHouse .listings-section-chevron { color: #a78bfa; }
.listings-section-header.section-openHouse .listings-section-count { background: #a78bfa; }

.listings-section-header.all-listings {
  cursor: default;
  background: #f3f4f6;
}

.listings-section-header.all-listings:hover {
  background: #f3f4f6;
}

.listings-section-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.listings-section-chevron {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: #16a34a;
  transition: transform 0.2s;
}

.listings-section-chevron.expanded {
  transform: rotate(90deg);
}

.listings-section-name {
  font-weight: 600;
  font-size: 0.75rem;
  color: #333;
}

.listings-section-count {
  background: #16a34a;
  color: white;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 0.65rem;
  font-weight: 600;
}

.listings-section-content {
  transition: max-height 0.3s ease, opacity 0.2s ease;
}

.listings-section-content.collapsed {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  margin-bottom: 0;
}

/* Expandable neighborhood row wrapper (for attribute sections) */
.listings-neighborhood-row-wrapper {
  margin-bottom: 8px;
}

.listings-neighborhood-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s;
}

.listings-neighborhood-row:hover {
  background: #f0fdf4;
  border-color: #22c55e;
}

.listings-nh-row-left {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex: 1;
  min-width: 0;
}

.listings-nh-row-chevron {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  transition: transform 0.2s;
  color: #6b7280;
}

.listings-nh-row-chevron.expanded {
  transform: rotate(90deg);
}

.listings-neighborhood-row-name {
  font-weight: 500;
  font-size: 0.85rem;
  color: #333;
}

.listings-nh-row-left:hover .listings-neighborhood-row-name {
  color: #16a34a;
}

.listings-neighborhood-row-count {
  font-size: 0.75rem;
  color: #6b7280;
  flex-shrink: 0;
}

/* Stacked heart/bell icons like property cards */
.listings-nh-row-actions-stacked {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}

/* Expandable content area for listings */
.listings-nh-row-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${MIN_CARD_COL_WIDTH}px, 1fr));
  gap: ${CARD_GRID_GAP}px;
  padding-left: 24px;
  padding-top: 8px;
  transition: max-height 0.3s ease, opacity 0.2s ease;
}

.listings-nh-row-content.collapsed {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  padding-top: 0;
}

@media (max-width: 1023px) {
  .listings-drawer-resize {
    display: none;
  }
}

@media (max-width: 767px) {
  .listings-handle {
    padding: 8px 6px;
  }

  .listings-handle-text {
    font-size: 0.7rem;
  }

  .listings-panel {
    width: 260px;
  }

  .listing-card {
    padding: 10px;
    gap: 10px;
  }

  .listing-card-image,
  .listing-card-image-placeholder {
    width: 70px;
    height: 52px;
  }

  .listing-card-price {
    font-size: 0.95rem;
  }

  .listing-card-address {
    font-size: 0.75rem;
  }

  .listing-card-neighborhood {
    font-size: 0.7rem;
  }

  .listing-card-stats {
    font-size: 0.7rem;
  }

  .listings-group-header {
    padding: 8px 10px;
    font-size: 0.75rem;
  }

  .listings-group-header.collapsed-preview {
    padding: 4px 8px;
    margin-bottom: 4px;
  }
}

@media (max-width: 480px) {
  .listings-panel {
    width: 240px;
  }

  .listing-card-image,
  .listing-card-image-placeholder {
    width: 60px;
    height: 45px;
  }
}

/* Touch device improvements */
@media (hover: none) and (pointer: coarse) {
  .listing-card {
    min-height: 72px;
  }

  .listing-action-btn {
    width: 36px;
    height: 36px;
  }

  .listing-action-btn svg {
    width: 20px;
    height: 20px;
  }

  .listings-handle {
    min-width: 44px;
  }

  .listings-nh-heart,
  .listings-nh-bell {
    width: 32px;
    height: 32px;
  }

  .listings-nh-heart svg,
  .listings-nh-bell svg {
    width: 18px;
    height: 18px;
  }
}
`;
export function injectStyles() {
    if (document.getElementById('listings-drawer-styles'))
        return;
    const style = document.createElement('style');
    style.id = 'listings-drawer-styles';
    style.textContent = listingsDrawerStyles;
    document.head.appendChild(style);
}
//# sourceMappingURL=styles.js.map